import React, { FC, ReactNode } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { AnyEntity } from "@mapmycustomers/shared/types/entity";
import DateRange from "@mapmycustomers/shared/types/range/DateRange";
import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";

import { getDatesRange } from "@app/enum/dashboard/dateRanges";
import {
  DrillDownModalProps,
  TabbedDrillDownModal,
} from "@app/scene/dashboard/components/cards/common/DrillDownModal";
import { fetchNewRoutesCardDrillDownData } from "@app/scene/dashboard/store/cards/newRoutes/actions";
import {
  getNewRoutesDrillDownCustomDateRange,
  getNewRoutesDrillDownTotalFilteredRoutes,
  getNewRoutesDrillDownViewState,
  isNewRoutesDrillDownLoading,
} from "@app/scene/dashboard/store/cards/newRoutes/selectors";
import { TabbedDrillDownListFetcherPayload } from "@app/scene/dashboard/types/TabbedDrillDownListFetcher";
import formatDrillDownDateRange from "@app/scene/dashboard/utils/formatDrillDownDateRange";
import { RootState } from "@app/store/rootReducer";
import EntityTypesSupportedInNewRoutes from "@app/types/dashboard/cards/newRoutes/EntityTypesSupportedInNewRoutes";
import NewRoutesCardConfiguration from "@app/types/dashboard/cards/newRoutes/NewRoutesCardConfiguration";

import { NEW_ROUTES_ENTITY_TYPES } from "../../consts";
import messages from "../../messages";

interface Props extends Pick<DrillDownModalProps, "onEdit" | "onHide"> {
  configuration: NewRoutesCardConfiguration;
  customDateRange?: DateRange;
  onFetchDrillDownData: (
    payload: TabbedDrillDownListFetcherPayload<AnyEntity, EntityTypesSupportedInNewRoutes>
  ) => void;
  totalFilteredRoutes: Record<EntityTypesSupportedInNewRoutes, number>;
  viewStates: Record<EntityTypesSupportedInNewRoutes, ViewState>;
}

const DrillDown: FC<Props> = ({
  configuration,
  customDateRange,
  onEdit,
  onFetchDrillDownData,
  onHide,
  totalFilteredRoutes,
  viewStates,
}) => {
  const intl = useIntl();

  return (
    <TabbedDrillDownModal<EntityTypesSupportedInNewRoutes>
      allEntityTypes={NEW_ROUTES_ENTITY_TYPES}
      entityTypes={
        configuration.criteria.entityTypes.length
          ? configuration.criteria.entityTypes
          : NEW_ROUTES_ENTITY_TYPES
      }
      onEdit={onEdit}
      onFetchDrillDownData={onFetchDrillDownData}
      onHide={onHide}
      subtitle={formatDrillDownDateRange(
        customDateRange ??
          getDatesRange(configuration.dateRange.range, configuration.dateRange.subRange)
      )}
      title={
        configuration.customTitle?.trim() ||
        intl.formatMessage(messages.cardTitle, {
          date: (text: ReactNode[]) => text,
          dateRange: configuration.dateRange.range,
          dateSubRange: configuration.dateRange.subRange,
          entityType:
            configuration.criteria.entityTypes.length === 1
              ? configuration.criteria.entityTypes[0]
              : undefined,
        })
      }
      totalFilteredRecords={totalFilteredRoutes}
      viewStates={viewStates}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  customDateRange: getNewRoutesDrillDownCustomDateRange(state),
  loading: isNewRoutesDrillDownLoading(state),
  totalFilteredRoutes: getNewRoutesDrillDownTotalFilteredRoutes(state),
  viewStates: getNewRoutesDrillDownViewState(state),
});

const mapDispatchToProps = {
  onFetchDrillDownData: fetchNewRoutesCardDrillDownData.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(DrillDown);
